import {
  QrCodeContentSettings,
  ResponsiveAssetsInfo,
  Template,
  TemplateCustomDestinationDto,
  TemplateDestinationDto,
  TemplatePreviewOptions,
  TemplateProperties,
  UrlContentSettings,
  VideoTemplateContentType
} from "../../models";
import {
  CfsDevicePreview,
  CfsDeviceType,
  DigitalAssetGetDto,
  DigitalAssetScreenSize,
  DisplayTypeIdAsset,
  GlobalTemplateType,
  KeyValueAsset,
  KeyValueDto,
  TemplateContentType
} from "clearline-api";
import { defaultCfsTemplateDisplayTime } from "./cfs-template.config";

const DisplayTypeToDeviceTypeMap: Record<string, CfsDeviceType> = {
  TabletHorizontal: CfsDeviceType.TabletHorizontal,
  TabletVertical: CfsDeviceType.TabletVertical,
  DisplayHorizontal: CfsDeviceType.Desktop,
  DisplayVertical: CfsDeviceType.DisplayVertical,
  KioskHorizontal: CfsDeviceType.Desktop,
  KioskVertical: CfsDeviceType.DisplayVertical,
  TerminalHorizontal: CfsDeviceType.TabletHorizontal,
  TerminalVertical: CfsDeviceType.Mobile
};

const DeviceTypeToScreenSizeMap: Record<CfsDeviceType, DigitalAssetScreenSize> = {
  [CfsDeviceType.Desktop]: DigitalAssetScreenSize.Full,
  [CfsDeviceType.DisplayVertical]: DigitalAssetScreenSize.Vertical,
  [CfsDeviceType.TabletHorizontal]: DigitalAssetScreenSize.Full,
  [CfsDeviceType.TabletVertical]: DigitalAssetScreenSize.Tablet,
  [CfsDeviceType.Mobile]: DigitalAssetScreenSize.Compact
};

export const isVimeoTemplate = (template: Template): boolean => {
  const { VideoContentType = null } = getTemplateUrlContentSettings(template) || {};

  return VideoContentType === VideoTemplateContentType.Vimeo;
};

export const isYoutubeTemplate = (template: Template): boolean => {
  const { VideoContentType = null } = getTemplateUrlContentSettings(template) || {};

  return VideoContentType === VideoTemplateContentType.YouTube;
};

export const isImportedVideoTemplate = (template: Template): boolean => {
  const { VideoContentType = null } = getTemplateUrlContentSettings(template) || {};

  return VideoContentType === VideoTemplateContentType.Import;
};

export const isVideoTemplate = (template: Template): boolean => {
  const { ContentType = null } = getTemplateUrlContentSettings(template) || {};

  return ContentType === "Video";
};

const getTemplateUrlContentSettings = (template: Template): UrlContentSettings | null => {
  if (template.templateContentType !== TemplateContentType.CustomContent) {
    return null;
  }

  return template.templateProperties as UrlContentSettings;
};

export const buildCfsDevicePreview = (displayTypeId: number): CfsDevicePreview => {
  const displayType: string =
    Object.keys(DisplayTypeIdAsset).find((key) => DisplayTypeIdAsset[key as keyof typeof DisplayTypeIdAsset] === displayTypeId) || "";
  const deviceType: CfsDeviceType = displayType
    ? DisplayTypeToDeviceTypeMap[displayType as keyof typeof CfsDeviceType]
    : CfsDeviceType.Desktop;
  const screenSize: DigitalAssetScreenSize = deviceType
    ? DeviceTypeToScreenSizeMap[deviceType as keyof typeof DeviceTypeToScreenSizeMap]
    : DigitalAssetScreenSize.Full;

  return {
    deviceType,
    screenSize
  };
};

export const isSystemCfsTemplate = (template: Template): boolean => {
  return template?.globalTemplateType === GlobalTemplateType.System;
};

export const rgbToHex = (rgbArray: [number, number, number], maxColorValue = 255): string => {
  const hexComponents: string[] = rgbArray.map((value) => {
    const hex = Math.round(maxColorValue * value).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  });

  return hexComponents?.length ? `#${hexComponents.join("")}` : "";
};

export const getTemplateDestination = (
  triggerDestination?: TemplateCustomDestinationDto,
  defaultDestination?: TemplateDestinationDto
): TemplateDestinationDto | undefined => {
  if (!triggerDestination || triggerDestination.useDefaultDestination) return defaultDestination;
  return triggerDestination.customDestination;
};

export const getContentDurationByTemplate = (template: Template): number => {
  const templateProperties: TemplateProperties | null = template?.templateProperties || null;

  return Number((templateProperties as UrlContentSettings)?.ContentDuration) ?? 0;
};

/** Returns CFS template display time in ms **/
export const getFullDisplayTimeByTemplate = (template: Template): number => {
  return template?.configuration?.options?.displayTime || getContentDurationByTemplate(template) || defaultCfsTemplateDisplayTime;
};

export const getDigitalAssetScreenSize = (isLowResolutionScreen: boolean, withTablet = false): DigitalAssetScreenSize => {
  const smallScreen = 768;
  const mobileScreen = 576;
  let updatedScreenSize: DigitalAssetScreenSize = DigitalAssetScreenSize.Full;

  if (!isLowResolutionScreen) {
    if (window.innerWidth <= smallScreen) {
      if (withTablet) {
        updatedScreenSize = window.innerWidth <= mobileScreen ? DigitalAssetScreenSize.Compact : DigitalAssetScreenSize.Tablet;
      } else {
        updatedScreenSize = DigitalAssetScreenSize.Compact;
      }
    } else if (window.innerHeight >= window.innerWidth) {
      updatedScreenSize = DigitalAssetScreenSize.Vertical;
    }
  }

  return updatedScreenSize;
};

export const getIsDigitalScreenSizeResponsive = (digitalAsset?: DigitalAssetGetDto): boolean => {
  return !!digitalAsset?.configuration?.screenSize && digitalAsset.configuration.screenSize !== DigitalAssetScreenSize.Full;
};

export const getLottieColorByHex = (hex: string): string => {
  const rgb = hexToRgb(hex);

  return rgb ? getLottieColorByRgb(rgb) : "";
};

export const getResponsiveAssetsInfo = (options: TemplatePreviewOptions): ResponsiveAssetsInfo => {
  const { digitalAsset } = options;

  return {
    [DigitalAssetScreenSize.Full]: digitalAsset,
    [DigitalAssetScreenSize.Compact]: null,
    [DigitalAssetScreenSize.Vertical]: null
  };
};

export const parseQrCodeSettingValue = (
  key: keyof QrCodeContentSettings,
  value: string
): QrCodeContentSettings[keyof QrCodeContentSettings] => {
  switch (key) {
    case "backgroundColor":
    case "buttonTextColor":
    case "buttonBackgroundColor":
    case "subtitleColor":
    case "qrActionColor":
    case "qrCodePrimaryColor":
    case "qrCodeSecondaryColor":
    case "qrCodeBackground":
    case "titleColor": {
      return rgbToHex(JSON.parse(value));
    }
    case "qrCodeHasFrame": {
      return value === "true";
    }
    case "buttonWidth":
    case "qrCodeLogoType": {
      return +value;
    }
    default:
      return value;
  }
};

export const getAllKeyValueAssetParams = (options?: TemplatePreviewOptions, digitalDefaultParams: KeyValueDto[] = []): KeyValueAsset => {
  const { defaultParameters, mediaContent } = options || {};

  const assetParameters: KeyValueAsset = getKeyValueAsset(digitalDefaultParams);
  const mediaParams: KeyValueAsset = getKeyValueAsset(mediaContent?.configuration.defaultParameters);

  return { ...assetParameters, ...mediaParams, ...defaultParameters };
};

export const capitalizeFirstLetter = (value: string): string => {
  if (value && typeof value === "string") {
    const firstLetter: string = value.slice(0, 1).toUpperCase();
    const restValue: string = value.slice(1);

    return `${firstLetter}${restValue}`;
  }

  return "";
};

// Private functions:
const hexToRgb = (hex: any) => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m: any, r: any, g: any, b: any) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
};

const getLottieColorByRgb = (rgb: any): string => {
  return Object.keys(rgb)
    .map((key: string) => {
      const rgbItem = rgb[key];
      const lottieColorPart = rgbItem ? rgbItem / 255 : 0;
      return `${lottieColorPart}`;
    })
    .join(",");
};

const getKeyValueAsset = (params: KeyValueDto[] = []): KeyValueAsset => {
  return params?.reduce((acc: KeyValueAsset, item: KeyValueDto) => {
    const { key, value } = item;
    acc[key] = value;

    return acc;
  }, {} as KeyValueAsset);
};
